/**
 * Inline Working Days Editor
 * Click to edit working days with toggle buttons in a popover
 */

import React, { useState } from 'react';
import {
  Box,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Check, Close, Edit, CalendarToday } from '@mui/icons-material';
import toast from 'react-hot-toast';
import driverService from '@/services/driverService';

const DAYS = [
  { value: 'monday', label: 'M', full: 'Mon' },
  { value: 'tuesday', label: 'T', full: 'Tue' },
  { value: 'wednesday', label: 'W', full: 'Wed' },
  { value: 'thursday', label: 'T', full: 'Thu' },
  { value: 'friday', label: 'F', full: 'Fri' },
  { value: 'saturday', label: 'S', full: 'Sat' },
  { value: 'sunday', label: 'S', full: 'Sun' },
];

interface InlineWorkingDaysEditorProps {
  driverId: string;
  currentDays: string[];
  onUpdate: () => void;
}

export function InlineWorkingDaysEditor({
  driverId,
  currentDays,
  onUpdate,
}: InlineWorkingDaysEditorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [days, setDays] = useState<string[]>(currentDays);
  const [saving, setSaving] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setDays(currentDays);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newDays: string[]) => {
    setDays(newDays);
  };

  const handleSave = async () => {
    if (days.length === 0) {
      toast.error('Select at least one working day');
      return;
    }

    setSaving(true);
    try {

      // Use working_days to match backend expected field name
      const result = await driverService.updateDriverDetails(driverId, {
        working_days: days,
      });

      toast.success('Working days updated');
      onUpdate();
      handleClose();
    } catch (error: any) {
      console.error('❌ Failed to update working days:', error);
      toast.error(error.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const open = Boolean(anchorEl);

  const displayText = currentDays.length > 0
    ? currentDays.length === 7
      ? 'All days'
      : currentDays.length >= 5
      ? `${currentDays.length} days/week`
      : currentDays.map(d => DAYS.find(day => day.value === d)?.full).join(', ')
    : 'Not set';

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.7 },
        }}
      >
        {currentDays.length > 0 ? (
          <>
            <Typography variant="body2">{displayText}</Typography>
            <Edit sx={{ fontSize: 14, color: 'text.secondary' }} />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 16 }} />
            Click to set
          </Typography>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Working Days
          </Typography>
          <ToggleButtonGroup
            value={days}
            onChange={handleChange}
            aria-label="working days"
            sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          >
            {DAYS.map((day) => (
              <ToggleButton
                key={day.value}
                value={day.value}
                aria-label={day.full}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  minWidth: 36,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  },
                }}
              >
                {day.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {days.length > 0 ? `${days.length} days selected` : 'Select at least one day'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClose} startIcon={<Close />}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving || days.length === 0}
              startIcon={saving ? <CircularProgress size={14} /> : <Check />}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
